// Non-destructive Confirm / Cancel modal (Design Constitution O02, O07, Q38).
// Parallels DeleteModal but without a typed word, for actions that lock data,
// move animals or reach outside the record (password reset, deactivate user,
// mark batch processed, retire housing). Rendered via the App-level
// `confirmAction` state — App holds the element, feature views trigger it via
// `window._wcfConfirm(message, onConfirm, label?)`.
//
// Look (O02): the question is the title ('Mark B-26-01 Complete?'), one gray
// consequence line under it, then Cancel and the green confirm, right-aligned.
// A string message is split at its first '?': the question becomes the title
// and the rest the consequence line. Esc and the backdrop cancel (O07).
// Focus starts on Cancel (Q38), so Enter never confirms by accident.
// The confirms layer stays at zIndex 11000 until every legacy pop-up (header
// overlays at 9000, processing at 6000–7000) moves onto the §3 ladder; a
// confirm opened from one of them must still sit on top.
import React from 'react';
import {buttonPrimary, buttonSecondary} from '../lib/buttonStyles.js';
import {
  MODAL_WIDTHS,
  dialogBodyStyle,
  dialogFooterStyle,
  dialogHeaderStyle,
  dialogMessageStyle,
  dialogPanelStyle,
  dialogScrimStyle,
  dialogTitleStyle,
} from '../lib/dialogStyles.js';
import {useModalFocusTrap} from './useModalFocusTrap.js';

// ConfirmContext is the only caller and builds this element with
// React.createElement, passing all four props from its `confirmAction` state:
// `msg` and `confirmLabel` come straight off that object (confirmLabel is
// optional there — the || below is what supplies 'Confirm'), and both callbacks
// are always supplied.
type ConfirmModalProps = {
  msg?: React.ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
};

// 'Mark B-26-01 Complete? It locks the batch.' → title + consequence line.
export function splitQuestion(msg: React.ReactNode): {title: React.ReactNode; line: React.ReactNode} {
  if (typeof msg !== 'string') return {title: msg, line: null};
  const at = msg.indexOf('?');
  if (at < 0 || at === msg.length - 1) return {title: msg, line: null};
  return {title: msg.slice(0, at + 1).trim(), line: msg.slice(at + 1).trim() || null};
}

const ConfirmModal = ({msg, confirmLabel, onConfirm, onCancel}: ConfirmModalProps) => {
  const label = confirmLabel || 'Confirm';
  const {dialogRef, handleDialogKeyDown} = useModalFocusTrap<HTMLDivElement>({onCancel});
  const confirmAndClose = () => {
    onConfirm();
    onCancel();
  };
  const {title, line} = splitQuestion(msg);

  return (
    <div
      data-confirm-modal="1"
      data-overlay-dismiss="enabled"
      data-focus-trap="active"
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-message"
      tabIndex={-1}
      onKeyDown={handleDialogKeyDown}
      onMouseDown={(e) => {
        // A click on the scrim must not move focus out of the dialog, or Esc
        // (handled on the dialog) would stop working (O06).
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        onCancel();
      }}
      style={{...dialogScrimStyle, zIndex: 11000}}
    >
      <div style={dialogPanelStyle(MODAL_WIDTHS.sm)}>
        <div style={dialogHeaderStyle}>
          <h2 id="confirm-modal-title" style={dialogTitleStyle}>
            {title}
          </h2>
        </div>
        <div style={dialogBodyStyle}>
          <p id="confirm-modal-message" style={dialogMessageStyle}>
            {line}
          </p>
        </div>
        <div style={{...dialogFooterStyle, justifyContent: 'flex-end'}}>
          <button
            type="button"
            autoFocus
            data-modal-initial-focus="1"
            className="wcf-clickable wcf-btn wcf-btn--secondary"
            onClick={onCancel}
            style={buttonSecondary}
          >
            Cancel
          </button>
          <button
            type="button"
            className="wcf-clickable wcf-btn wcf-btn--primary"
            onClick={confirmAndClose}
            style={buttonPrimary}
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
